import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Search, Box, ExternalLink, Server, Play, Square, RotateCcw, RefreshCw, Layers, FileCode } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { appsApi, App, AppStatus, Container } from '../../lib/api/apps';
import { serversApi } from '../../lib/api/servers';
import { Card, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';

const statusColors: Record<AppStatus, string> = {
  active: 'bg-green-100 text-green-700',
  inactive: 'bg-gray-100 text-gray-700',
  deploying: 'bg-blue-100 text-blue-700',
  failed: 'bg-red-100 text-red-700',
  maintenance: 'bg-yellow-100 text-yellow-700',
};

const containerStateColors: Record<string, string> = {
  running: 'bg-green-100 text-green-700',
  exited: 'bg-red-100 text-red-700',
  paused: 'bg-yellow-100 text-yellow-700',
  created: 'bg-gray-100 text-gray-700',
};

type Tab = 'apps' | 'containers';

export function AppsPage() {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [tab, setTab] = useState<Tab>('apps');
  const [selectedServer, setSelectedServer] = useState<string>('');
  const queryClient = useQueryClient();

  const { data: apps, isLoading: appsLoading } = useQuery({
    queryKey: ['apps', page, search],
    queryFn: () => appsApi.list({ page, limit: 20, search: search || undefined }),
    enabled: tab === 'apps',
  });

  const { data: servers } = useQuery({
    queryKey: ['servers'],
    queryFn: () => serversApi.list({ limit: 100 }),
  });

  const { data: containers, isLoading: containersLoading, refetch: refetchContainers } = useQuery({
    queryKey: ['containers', selectedServer],
    queryFn: () => appsApi.listContainers(selectedServer),
    enabled: tab === 'containers' && !!selectedServer,
  });

  const startApp = useMutation({
    mutationFn: (appId: string) => appsApi.startApp(appId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['apps'] });
      refetchContainers();
    },
  });

  const stopApp = useMutation({
    mutationFn: (appId: string) => appsApi.stopApp(appId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['apps'] });
      refetchContainers();
    },
  });

  const restartApp = useMutation({
    mutationFn: (appId: string) => appsApi.restartApp(appId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['apps'] });
      refetchContainers();
    },
  });

  // Set default server when servers load
  useEffect(() => {
    if (servers?.items.length && !selectedServer) {
      setSelectedServer(servers.items[0].id);
    }
  }, [servers, selectedServer]);

  const isLoading = tab === 'apps' ? appsLoading : containersLoading;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Applications</h1>
          <p className="text-gray-600">Manage your deployed applications and containers</p>
        </div>
        <Link to="/apps/new">
          <Button>
            <Plus className="h-4 w-4 mr-2" />
            New App
          </Button>
        </Link>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex gap-6">
          <button
            onClick={() => setTab('apps')}
            className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
              tab === 'apps'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Box className="h-4 w-4 inline mr-2" />
            Apps
          </button>
          <button
            onClick={() => setTab('containers')}
            className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
              tab === 'containers'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Layers className="h-4 w-4 inline mr-2" />
            Containers
          </button>
        </nav>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        {tab === 'apps' && (
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search apps..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
        )}
        {tab === 'containers' && (
          <>
            <select
              value={selectedServer}
              onChange={(e) => setSelectedServer(e.target.value)}
              className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 min-w-[200px]"
            >
              <option value="">Select a server</option>
              {servers?.items.map((server) => (
                <option key={server.id} value={server.id}>
                  {server.name} ({server.host})
                </option>
              ))}
            </select>
            <Button variant="outline" size="sm" onClick={() => refetchContainers()}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </>
        )}
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <Card key={i}>
              <CardContent className="p-4">
                <div className="animate-pulse space-y-3">
                  <div className="h-4 bg-gray-200 rounded w-3/4" />
                  <div className="h-3 bg-gray-200 rounded w-1/2" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : tab === 'apps' ? (
        /* Apps List */
        apps?.items.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Box className="h-12 w-12 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No applications</h3>
              <p className="text-gray-500 mb-4">Get started by creating your first app.</p>
              <Link to="/apps/new">
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  New App
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {apps?.items.map((app: App) => (
                <Link key={app.id} to={`/apps/${app.id}`}>
                  <Card className="hover:border-primary-300 transition-colors cursor-pointer h-full">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${app.app_type === 'static' ? 'bg-purple-100' : 'bg-primary-100'}`}>
                            {app.app_type === 'static' ? (
                              <FileCode className="h-5 w-5 text-purple-600" />
                            ) : (
                              <Box className="h-5 w-5 text-primary-600" />
                            )}
                          </div>
                          <div>
                            <h3 className="font-medium text-gray-900">{app.name}</h3>
                            <p className="text-sm text-gray-500">{app.slug}</p>
                          </div>
                        </div>
                        <div className="flex flex-col gap-1 items-end">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${app.app_type === 'static' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                            {app.app_type}
                          </span>
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusColors[app.status]}`}>
                            {app.status}
                          </span>
                        </div>
                      </div>
                      {app.description && (
                        <p className="mt-3 text-sm text-gray-600 line-clamp-2">{app.description}</p>
                      )}
                      {app.domain && (
                        <div className="mt-3 flex items-center gap-1 text-sm text-primary-600">
                          <ExternalLink className="h-3 w-3" />
                          {app.domain}
                        </div>
                      )}
                      <div className="mt-3 text-xs text-gray-400">
                        Updated {new Date(app.updated_at).toLocaleDateString()}
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>

            {apps && apps.pagination.total_pages > 1 && (
              <div className="flex justify-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Previous
                </Button>
                <span className="flex items-center px-3 text-sm text-gray-600">
                  Page {page} of {apps.pagination.total_pages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={page >= apps.pagination.total_pages}
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )
      ) : (
        /* Containers List */
        !selectedServer ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Server className="h-12 w-12 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Select a server</h3>
              <p className="text-gray-500">Choose a server to view its containers.</p>
            </CardContent>
          </Card>
        ) : containers?.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Layers className="h-12 w-12 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No containers found</h3>
              <p className="text-gray-500">This server has no Docker containers running.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {containers?.map((container: Container) => (
              <Card key={container.container_id} className="h-full">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${container.state === 'running' ? 'bg-green-100' : 'bg-gray-100'}`}>
                        <Layers className={`h-5 w-5 ${container.state === 'running' ? 'text-green-600' : 'text-gray-600'}`} />
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900">{container.name}</h3>
                        <p className="text-sm text-gray-500 truncate max-w-[150px]">{container.image}</p>
                      </div>
                    </div>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${containerStateColors[container.state] || 'bg-gray-100 text-gray-700'}`}>
                      {container.state}
                    </span>
                  </div>

                  <div className="mt-3 text-xs text-gray-500">
                    <div>ID: {container.container_id.slice(0, 12)}</div>
                    {container.ports.length > 0 && (
                      <div className="mt-1">Ports: {container.ports.slice(0, 2).join(', ')}{container.ports.length > 2 && '...'}</div>
                    )}
                  </div>

                  {/* Linked App */}
                  {container.app ? (
                    <Link
                      to={`/apps/${container.app.id}`}
                      className="mt-3 block p-2 rounded bg-primary-50 border border-primary-200 hover:bg-primary-100 transition-colors"
                    >
                      <div className="flex items-center gap-2 text-sm">
                        <Box className="h-4 w-4 text-primary-600" />
                        <span className="text-primary-700 font-medium">{container.app.name}</span>
                      </div>
                    </Link>
                  ) : (
                    <div className="mt-3 p-2 rounded bg-gray-50 border border-gray-200">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">Not linked to an app</span>
                        <Link to={`/apps/new?container=${encodeURIComponent(container.name)}&server=${selectedServer}&image=${encodeURIComponent(container.image)}${container.compose_path ? `&compose_path=${encodeURIComponent(container.compose_path)}` : ''}${container.domain ? `&domain=${encodeURIComponent(container.domain)}` : ''}${container.compose_service ? `&service=${encodeURIComponent(container.compose_service)}` : ''}`}>
                          <Button variant="ghost" size="sm">
                            <Plus className="h-3 w-3 mr-1" />
                            Link
                          </Button>
                        </Link>
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  {container.app && (
                    <div className="mt-3 flex gap-2">
                      {container.state === 'running' ? (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.preventDefault();
                              stopApp.mutate(container.app!.id);
                            }}
                            disabled={stopApp.isPending}
                          >
                            <Square className="h-3 w-3 mr-1" />
                            Stop
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.preventDefault();
                              restartApp.mutate(container.app!.id);
                            }}
                            disabled={restartApp.isPending}
                          >
                            <RotateCcw className="h-3 w-3 mr-1" />
                            Restart
                          </Button>
                        </>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.preventDefault();
                            startApp.mutate(container.app!.id);
                          }}
                          disabled={startApp.isPending}
                        >
                          <Play className="h-3 w-3 mr-1" />
                          Start
                        </Button>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )
      )}
    </div>
  );
}
